import React, { Suspense, useMemo } from "react";
import { Spinner } from "@/components/ui";
import { EmptyView } from "@/components/EmptyView";

const ResponsiveBar = React.lazy(() => import("@nivo/bar").then(m => ({ default: m.ResponsiveBar })));

interface ChartProps {
  data: Array<{
    date: string;
    count: number;
  }>;
  title?: string;
}

export const ClicksByDayChart: React.FC<ChartProps> = ({ data }) => {
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const sortedData = [...data].sort((a, b) => a.date.localeCompare(b.date));
    const firstDate = new Date(sortedData[0].date + "T00:00:00");
    const lastDate = new Date(sortedData[sortedData.length - 1].date + "T00:00:00");
    const diffDays = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));

    let interval: "day" | "week" | "month" = "day";
    if (diffDays > 365) interval = "month";
    else if (diffDays > 60) interval = "week";

    if (interval === "day") {
      return sortedData.map(d => {
        const date = new Date(d.date + "T00:00:00");
        return {
          label: `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
            .getDate()
            .toString()
            .padStart(2, "0")}/${date.getFullYear().toString().slice(-2)}`,
          clicks: d.count,
        };
      });
    }

    const groups: Record<string, number> = {};
    sortedData.forEach(d => {
      const date = new Date(d.date + "T00:00:00");
      let key = "";
      if (interval === "week") {
        // Find start of week (Sunday)
        const day = date.getDay();
        const diff = date.getDate() - day;
        const weekStart = new Date(date.setDate(diff));
        key = `Week of ${(weekStart.getMonth() + 1).toString().padStart(2, "0")}/${weekStart
          .getDate()
          .toString()
          .padStart(2, "0")}`;
      } else {
        key = date.toLocaleString("default", { month: "short", year: "2-digit" });
      }
      groups[key] = (groups[key] || 0) + d.count;
    });

    return Object.entries(groups).map(([label, clicks]) => ({ label, clicks }));
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="py-8">
        <EmptyView text="No click data available for this time range" />
      </div>
    );
  }

  return (
    <div className="h-[300px] w-full">
      <Suspense
        fallback={
          <div className="h-full flex items-center justify-center">
            <Spinner />
          </div>
        }
      >
        <ResponsiveBar
          data={processedData}
          keys={["clicks"]}
          indexBy="label"
          margin={{ top: 10, right: 10, bottom: 50, left: 50 }}
          padding={0.3}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={{ scheme: "paired" }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: processedData.length > 10 ? -45 : 0,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Clicks",
            legendPosition: "middle",
            legendOffset: -40,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          role="application"
          ariaLabel="Click performance chart"
        />
      </Suspense>
    </div>
  );
};
